import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import NavTime from "./components/NavTime";
import OpeningHG from "./components/OpeningHG";
import OpeningH from "./components/OpeningH";
import WorkingHG from "./components/WorkingHG";
import WorkingH from "./components/WorkingH";
import ManageRoomTab from "./newTabs/ManageRoomTab";
import GeneralOpeningHoursTab from "./newTabs/GeneralOpeningHoursTab";
import OpeningHoursTab from "./newTabs/OpeningHoursTab";
import DentistAbsentDateTab from "./newTabs/DentistAbsentDateTab";
import DentistGeneralHoursTab from "./newTabs/DentistGeneralHoursTab";
import { ConfirmationModal } from "../bookui/components/ConfirmationModal";
import AppointmentSlotDurationSetting from "./components/AppointmentSlotDurationSetting";
import baseUrl from "../../../api/baseUrl";
import { isRoomAllocationEnabled } from "../../appointment/viewappointment/components/appointmentViewShared";
const weekArray = [
  { week: "Monday", abbreviation: "Mon" },
  { week: "Tuesday", abbreviation: "Tue" },
  { week: "Wednesday", abbreviation: "Wed" },
  { week: "Thursday", abbreviation: "Thu" },
  { week: "Friday", abbreviation: "Fri" },
  { week: "Saturday", abbreviation: "Sat" },
  { week: "Sunday", abbreviation: "Sun" },
];

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const generateTimeSlots = (
  formData: { from: string; to: string; timeDuration: string },
  formatTime: (date: Date) => string
) => {
  const startTime = new Date(`01/01/2000 ${formData.from}`);
  const endTime = new Date(`01/01/2000 ${formData.to}`);
  const duration = parseInt(formData.timeDuration, 10);

  const timeSlotsArray: {
    formattedTime: string;
    startTime: string;
    endTime: string;
    interval: number;
  }[] = [];
  let currentTime = startTime;

  while (currentTime <= endTime) {
    const formattedTime = formatTime(currentTime);
    const formattedStartTime = formatTime(currentTime);

    const endTimeSlot = new Date(currentTime);
    endTimeSlot.setMinutes(endTimeSlot.getMinutes() + duration);
    const formattedEndTime = formatTime(endTimeSlot);

    timeSlotsArray.push({
      formattedTime,
      startTime: formattedStartTime,
      endTime: formattedEndTime,
      interval: duration,
    });

    currentTime.setMinutes(currentTime.getMinutes() + duration);
  }

  return timeSlotsArray;
};

function Managetime() {
  const roomAllocationEnabled = isRoomAllocationEnabled();

  const [formData, setformData] = useState({
    from: "09:00",
    to: "17:00",
    timeDuration: "30",
  });

  const [tabMode, setTabMode] = useState<"time" | "new">(
    roomAllocationEnabled ? "new" : "time"
  );
  const [doctorId, setdoctorId] = useState("");
  const [dentists, setdentists] = useState([]);
  const [activeTab, setActiveTab] = useState<
    "opening_hg" | "opening_h" | "working_hg" | "working_h"
  >("opening_hg");
  const [activeNewTab, setActiveNewTab] = useState<
    | "manage_room"
    | "general_opening"
    | "opening"
    | "dentist_absent"
    | "dentist_general"
  >("manage_room");
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const handleResetAll = useCallback(async () => {
    if (isResetting) return;

    setIsResetting(true);
    try {
      const res = await baseUrl.post("/client/setting/manageTime/reset");
      toast.success(res.data?.message || "All schedule data has been reset");
      setResetKey((k) => k + 1);
      setIsResetConfirmOpen(false);
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string; error?: string } };
      };
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to reset schedule data";
      toast.error(msg);
    } finally {
      setIsResetting(false);
    }
  }, [isResetting]);

  useEffect(() => {
    baseUrl
      .get("/appointment/findAllAppointmentDentist")
      .then((response) => {
        setdentists(response.data.data);
        setdoctorId(response.data.data[0]._id);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);
  useEffect(() => {
    baseUrl
      .get("/clinic/getTimeData")
      .then((response) => {
        if (response.data.data.startTime) {
          setformData((prevData: any) => ({
            ...prevData,
            from: response.data.data.startTime,
            to: response.data.data.endTime,
          }));
         
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, [setformData]);

  const timeArray = useMemo(
    () => generateTimeSlots(formData, formatTime),
    [formData]
  );

  return (
    <>
      <div className="mt-2 sm:mt-4 space-y-3 sm:space-y-6 px-2 sm:px-0 text-slate-800 dark:text-slate-200">
        <AppointmentSlotDurationSetting />

        <div className="flex flex-col gap-2 border-b border-slate-200 dark:border-slate-700 p-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            {roomAllocationEnabled && (
              <label className="inline-flex items-center gap-3 select-none">
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  By Duration
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={tabMode === "new"}
                  onClick={() =>
                    setTabMode((m) => (m === "new" ? "time" : "new"))
                  }
                  className={[
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900",
                    tabMode === "new"
                      ? "bg-primary"
                      : "bg-slate-300 dark:bg-slate-700",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow",
                      tabMode === "new" ? "translate-x-5" : "translate-x-1",
                    ].join(" ")}
                  />
                </button>
              </label>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setIsResetConfirmOpen(true)}
                disabled={isResetting}
                className="px-3 py-1.5 rounded-md text-sm font-semibold border border-red-300 text-red-700 hover:bg-red-50 active:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors outline-none focus-visible:ring-2 focus-visible:ring-red-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/40 dark:active:bg-red-950/60 dark:focus-visible:ring-offset-slate-900"
              >
                Reset all
              </button>
              <button
                type="button"
                onClick={() => setIsTimeModalOpen(true)}
                className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200 dark:active:bg-slate-300 dark:focus-visible:ring-offset-slate-900"
              >
                Clinic Hours
              </button>
            </div>
          </div>

          {(roomAllocationEnabled ? tabMode : "time") === "time" ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveTab("opening_hg")}
                className={[
                  "px-3 py-1.5 rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 active:scale-[0.98]",
                  activeTab === "opening_hg"
                    ? "bg-primary text-white shadow-sm hover:bg-primary/90 active:bg-primary/80"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:active:bg-slate-600",
                ].join(" ")}
              >
                Opening (General)
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("opening_h")}
                className={[
                  "px-3 py-1.5 rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 active:scale-[0.98]",
                  activeTab === "opening_h"
                    ? "bg-primary text-white shadow-sm hover:bg-primary/90 active:bg-primary/80"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:active:bg-slate-600",
                ].join(" ")}
              >
                Closing Hours
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("working_hg")}
                className={[
                  "px-3 py-1.5 rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 active:scale-[0.98]",
                  activeTab === "working_hg"
                    ? "bg-primary text-white shadow-sm hover:bg-primary/90 active:bg-primary/80"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:active:bg-slate-600",
                ].join(" ")}
              >
                Working (General)
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("working_h")}
                className={[
                  "px-3 py-1.5 rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 active:scale-[0.98]",
                  activeTab === "working_h"
                    ? "bg-primary text-white shadow-sm hover:bg-primary/90 active:bg-primary/80"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:active:bg-slate-600",
                ].join(" ")}
              >
                Working
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveNewTab("manage_room")}
                className={[
                  "px-3 py-1.5 rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 active:scale-[0.98]",
                  activeNewTab === "manage_room"
                    ? "bg-primary text-white shadow-sm hover:bg-primary/90 active:bg-primary/80"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:active:bg-slate-600",
                ].join(" ")}
              >
                Manage Room
              </button>
         
              <button
                type="button"
                onClick={() => setActiveNewTab("general_opening")}
                className={[
                  "px-3 py-1.5 rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 active:scale-[0.98]",
                  activeNewTab === "general_opening"
                    ? "bg-primary text-white shadow-sm hover:bg-primary/90 active:bg-primary/80"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:active:bg-slate-600",
                ].join(" ")}
              >
                General Opening hours
              </button>
              <button
                type="button"
                onClick={() => setActiveNewTab("opening")}
                className={[
                  "px-3 py-1.5 rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 active:scale-[0.98]",
                  activeNewTab === "opening"
                    ? "bg-primary text-white shadow-sm hover:bg-primary/90 active:bg-primary/80"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:active:bg-slate-600",
                ].join(" ")}
              >
                 Closing Hours
              </button>

              <button
                type="button"
                onClick={() => setActiveNewTab("dentist_general")}
                className={[
                  "px-3 py-1.5 rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 active:scale-[0.98]",
                  activeNewTab === "dentist_general"
                    ? "bg-primary text-white shadow-sm hover:bg-primary/90 active:bg-primary/80"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:active:bg-slate-600",
                ].join(" ")}
              >
                Dentist General hours
              </button>
              <button
                type="button"
                onClick={() => setActiveNewTab("dentist_absent")}
                className={[
                  "px-3 py-1.5 rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 active:scale-[0.98]",
                  activeNewTab === "dentist_absent"
                    ? "bg-primary text-white shadow-sm hover:bg-primary/90 active:bg-primary/80"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:active:bg-slate-600",
                ].join(" ")}
              >
                Dentist absent date
              </button>
            </div>
          )}
        </div>



        <div key={resetKey}>
          {(roomAllocationEnabled ? tabMode : "time") === "time" ? (
            <>
              {activeTab === "opening_hg" && (
                <OpeningHG timeData={timeArray} weekArray={weekArray} />
              )}
              {activeTab === "opening_h" && <OpeningH timeData={timeArray} />}
              {activeTab === "working_hg" && (
                <WorkingHG
                  timeData={timeArray}
                  doctorId={doctorId}
                  setdoctorId={setdoctorId}
                  dentists={dentists}
                  weekArray={weekArray}
                />
              )}
              {activeTab === "working_h" && (
                <WorkingH
                  timeData={timeArray}
                  doctorId={doctorId}
                  setdoctorId={setdoctorId}
                  dentists={dentists}
                />
              )}
            </>
          ) : (
            <>
              {activeNewTab === "manage_room" && <ManageRoomTab />}
              {activeNewTab === "general_opening" && (
                <GeneralOpeningHoursTab
                  clinicSessionStart={formData.from}
                  clinicSessionEnd={formData.to}
                />
              )}
              {activeNewTab === "opening" && <OpeningHoursTab />}
              {activeNewTab === "dentist_absent" && <DentistAbsentDateTab />}
              {activeNewTab === "dentist_general" && <DentistGeneralHoursTab />}
            </>
          )}
        </div>
      </div>
      {isResetConfirmOpen && (
        <ConfirmationModal
          title="Reset all schedule data?"
          message="This will permanently delete all rooms, general opening hours, closing hours, dentist general hours, and dentist absent dates for this clinic. This action cannot be undone."
          confirmText={isResetting ? "Resetting…" : "Reset all"}
          cancelText="Cancel"
          isLoading={isResetting}
          onConfirm={handleResetAll}
          onCancel={() => setIsResetConfirmOpen(false)}
        />
      )}
      {isTimeModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsTimeModalOpen(false)}
            aria-label="Close modal"
          />
          <div className="relative w-full max-w-sm sm:max-w-md">
            <div className="rounded-xl bg-transparent">
              <NavTime
                setformData={setformData}
                formData={formData}
                onClose={() => setIsTimeModalOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Managetime;
